import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import { cleanerError } from "@/lib/utils";
import {
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
} from "@/redux/services/inventory/supplier.api";
import { Supplier } from "@/redux/services/inventory/inventory.type";
import { toastApiError } from "@/lib/apiErrors";

setLocale(fr);

export interface SupplierFormData {
  name: string;
  contactName?: string;
  phone?: string;
  email?: string;
  paymentTerms?: string;
  leadTimeDays?: number | null;
  notes?: string;
}

export const supplierValidationSchema: yup.ObjectSchema<SupplierFormData> = yup
  .object()
  .shape({
    name: yup.string().required().label("Nom"),
    contactName: yup.string().optional().label("Contact"),
    phone: yup.string().optional().label("Téléphone"),
    email: yup
      .string()
      .email("Ce champ doit être une adresse e-mail valide")
      .optional(),
    paymentTerms: yup.string().optional().label("Conditions de paiement"),
    leadTimeDays: yup
      .number()
      .transform((value, original) =>
        original === "" || original === null || original === undefined
          ? null
          : value
      )
      .nullable()
      .min(0, "Le délai doit être positif")
      .integer("Le délai doit être un nombre entier")
      .optional()
      .label("Délai de livraison"),
    notes: yup.string().optional().max(1000).label("Notes"),
  });

export const useCrudSupplier = (
  closeModal: () => void,
  restaurantId: string,
  item?: Supplier,
  onCreated?: (supplier: Supplier) => void
) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    watch,
    setValue,
  } = useForm<SupplierFormData>({
    resolver: yupResolver(supplierValidationSchema),
    defaultValues: {
      name: item?.name ?? "",
      contactName: item?.contactName ?? "",
      phone: item?.phone ?? "",
      email: item?.email ?? "",
      paymentTerms: item?.paymentTerms ?? "",
      leadTimeDays: item?.leadTimeDays ?? null,
      notes: item?.notes ?? "",
    },
  });

  const [createSupplier, { isLoading: isCreating }] =
    useCreateSupplierMutation();
  const [updateSupplier, { isLoading: isUpdating }] =
    useUpdateSupplierMutation();
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    reset({
      name: item?.name ?? "",
      contactName: item?.contactName ?? "",
      phone: item?.phone ?? "",
      email: item?.email ?? "",
      paymentTerms: item?.paymentTerms ?? "",
      leadTimeDays: item?.leadTimeDays ?? null,
      notes: item?.notes ?? "",
    });
  }, [item, reset]);

  useEffect(() => {
    if (errors) cleanerError(errors, clearErrors);
  }, [errors, clearErrors]);

  const onSubmit = async (data: SupplierFormData) => {
    const payload = {
      name: data.name.trim(),
      contactName: data.contactName?.trim() || null,
      phone: data.phone?.trim() || null,
      email: data.email?.trim() || null,
      paymentTerms: data.paymentTerms?.trim() || null,
      leadTimeDays:
        data.leadTimeDays === null || data.leadTimeDays === undefined
          ? null
          : data.leadTimeDays,
      notes: data.notes?.trim() || null,
    };

    const res = item?.id
      ? await updateSupplier({ supplierUuid: item.id, data: payload })
      : await createSupplier({ restaurantId, data: payload });

    if ("data" in res) {
      toast.success(
        item?.id
          ? "Fournisseur modifié avec succès"
          : "Fournisseur créé avec succès"
      );
      if (!item?.id && res.data) onCreated?.(res.data);
      reset();
      closeModal();
    } else if ("error" in res) {
      toastApiError(res.error);
    }
  };

  return {
    register,
    errors,
    watch,
    setValue,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
  };
};
